import { useState } from "react";
import {
  RATES,
  SYMBOLS,
  FLAGS,
  convertPrice,
  ALL_PACKAGES,
  SERVICES_ORDER
} from "../data/packagesData";

export default function QuoteForm({ onCloseModal }) {
  const [currency, setCurrency] = useState("AED");
  const [selectedPkg, setSelectedPkg] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const pkg = ALL_PACKAGES.find(p => `${p.service}||${p.name}` === selectedPkg);
  const priceDisplay = pkg
    ? `${SYMBOLS[currency]} ${convertPrice(pkg.price, currency)}`
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    const form = e.target;
    const payload = {
      id: Date.now(),
      submittedAt: new Date().toISOString(),
      name: form.quoteName?.value || "",
      email: form.quoteEmail?.value || "",
      mobile: form.quoteMobile?.value || "",
      subject: form.quoteSubject?.value || "",
      package: pkg?.name || (selectedPkg === "others||Others" ? "Others / Custom" : ""),
      service: pkg?.service || "Others",
      currency,
      estimatedPrice: priceDisplay || "Custom",
      message: form.quoteMessage?.value || "",
    };

    try {
      // Save to localStorage as a backup/history
      const existingQuotes = JSON.parse(localStorage.getItem('Callsoftbd_quotes') || '[]');
      const updatedQuotes = [...existingQuotes, payload];
      localStorage.setItem('Callsoftbd_quotes', JSON.stringify(updatedQuotes));

      // Send to the PHP backend
      const response = await fetch('/api/quote.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Server error');
      }

      console.log("Quote saved successfully to server:", result.message);

      setSubmitted(true);
    } catch (err) {
      console.error("Quote save error:", err);
      setError("Failed to save quote. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setError("");
    if (onCloseModal) {
      onCloseModal();
    }
  };

  if (submitted) {
    return (
      <div className="quote-success-message active" style={{ padding: "40px 20px", textAlign: "center" }}>
        <div className="success-icon" style={{ fontSize: "40px", color: "#28a745", marginBottom: "15px" }}>
          <i className="fas fa-check-circle"></i>
        </div>
        <h4>Quote Request Sent!</h4>
        <p>Thank you! We'll be in touch shortly.</p>
        <button type="button" className="btn-common secondary-btn global-btn-style quote-done-btn mt-3" onClick={handleReset}>
          <span>{onCloseModal ? "Close Modal" : "Send Another Request"}</span>
        </button>
      </div>
    );
  }

  return (
    <form id="quoteForm" onSubmit={handleSubmit}>
      <div className="quote-form-row" style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: "15px" }}>
        <div className="form-group" style={{ flex: "0 0 auto", minWidth: "160px" }}>
          <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", fontSize: "14px", color: "#333" }}>Price Currency</label>
          <div style={{ display: "flex", border: "1px solid #ddd", borderRadius: "6px", overflow: "hidden", height: "42px" }}>
            {["AED", "USD", "EUR", "BDT"].map(cur => (
              <button
                key={cur}
                type="button"
                onClick={() => setCurrency(cur)}
                style={{
                  flex: 1, border: "none", cursor: "pointer",
                  fontWeight: 700, fontSize: "0.78rem",
                  fontFamily: "'Barlow', sans-serif",
                  transition: "all 0.2s",
                  background: currency === cur ? "var(--color-primary, #2400A8)" : "#f8f8f8",
                  color: currency === cur ? "#fff" : "#555",
                  borderRight: cur !== "EUR" ? "1px solid #ddd" : "none",
                }}
              >
                {FLAGS[cur]}&nbsp;{cur}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group" style={{ flex: 1, minWidth: "250px" }}>
          <label htmlFor="quotePackage" style={{ display: "block", marginBottom: "8px", fontWeight: "600", fontSize: "14px", color: "#333" }}>Package / Service Required</label>
          <select
            className="form-control"
            id="quotePackage"
            required
            value={selectedPkg}
            onChange={e => setSelectedPkg(e.target.value)}
            style={{ height: "42px" }}
          >
            <option value="">— Select a Package —</option>
            {SERVICES_ORDER.map(svc => (
              <optgroup key={svc} label={`── ${svc}`}>
                {ALL_PACKAGES
                  .filter(p => p.service === svc)
                  .map(p => (
                    <option key={`${p.service}||${p.name}`} value={`${p.service}||${p.name}`}>
                      {p.name}
                    </option>
                  ))
                }
              </optgroup>
            ))}
            <optgroup label="── Other">
              <option value="others||Others">Others / Custom Requirement</option>
            </optgroup>
          </select>
        </div>
      </div>

      {priceDisplay && selectedPkg !== "others||Others" && (
        <div style={{
          background: "linear-gradient(135deg, #f0eeff 0%, #e8f0ff 100%)",
          border: "1px solid rgba(36,0,168,0.15)",
          borderRadius: "8px", padding: "12px 18px",
          marginBottom: "20px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: "12px", flexWrap: "wrap"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <i className="fa fa-tag" style={{ color: "var(--color-primary, #2400A8)", fontSize: "1.1rem" }}></i>
            <div>
              <div style={{ fontSize: "0.75rem", color: "#666", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Estimated Package Price
              </div>
              <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "var(--color-primary, #2400A8)", lineHeight: 1.2 }}>
                {priceDisplay}
              </div>
            </div>
          </div>
          <div style={{ fontSize: "0.72rem", color: "#888", fontStyle: "italic", textAlign: "right", maxWidth: "160px", lineHeight: "1.4" }}>
            {currency !== "AED" ? `≈ AED ${pkg.price} · Rate indicative` : "Price in AED · VAT may apply"}
          </div>
        </div>
      )}

      <div className="quote-form-row" style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: "15px" }}>
        <div className="form-group" style={{ flex: 1, minWidth: "200px" }}>
          <label htmlFor="quoteName" style={{ display: "block", marginBottom: "8px", fontWeight: "600", fontSize: "14px", color: "#333" }}>Full Name</label>
          <input type="text" className="form-control" id="quoteName" placeholder="Your full name" required style={{ height: "42px" }} />
        </div>
        <div className="form-group" style={{ flex: 1, minWidth: "200px" }}>
          <label htmlFor="quoteEmail" style={{ display: "block", marginBottom: "8px", fontWeight: "600", fontSize: "14px", color: "#333" }}>Email Address</label>
          <input type="email" className="form-control" id="quoteEmail" placeholder="you@example.com" required style={{ height: "42px" }} />
        </div>
        <div className="form-group" style={{ flex: 1, minWidth: "200px" }}>
          <label htmlFor="quoteMobile" style={{ display: "block", marginBottom: "8px", fontWeight: "600", fontSize: "14px", color: "#333" }}>Mobile Number</label>
          <input type="tel" className="form-control" id="quoteMobile" placeholder="+971 XX XXX XXXX" required style={{ height: "42px" }} />
        </div>
      </div>

      <div className="form-group" style={{ marginBottom: "15px" }}>
        <label htmlFor="quoteSubject" style={{ display: "block", marginBottom: "8px", fontWeight: "600", fontSize: "14px", color: "#333" }}>Subject</label>
        <input type="text" className="form-control" id="quoteSubject" placeholder="Subject of your inquiry" required style={{ height: "42px" }} />
      </div>

      <div className="form-group" style={{ marginBottom: "20px" }}>
        <label htmlFor="quoteMessage" style={{ display: "block", marginBottom: "8px", fontWeight: "600", fontSize: "14px", color: "#333" }}>Message</label>
        <textarea className="form-control" id="quoteMessage" rows="3" placeholder="Tell us about your IT requirements..." style={{ resize: "vertical" }}></textarea>
      </div>

      {error && (
        <div style={{
          background: "#fff2f2", border: "1px solid #ffcccc", borderRadius: "6px",
          padding: "10px 14px", marginBottom: "12px", color: "#cc0000",
          fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "8px"
        }}>
          <i className="fa fa-exclamation-circle"></i> {error}
        </div>
      )}

      <div className="quote-submit-wrap" style={{ textAlign: "center" }}>
        <button
          type="submit"
          className="btn-common primary-btn global-btn-style"
          disabled={submitting}
          style={{ opacity: submitting ? 0.75 : 1, cursor: submitting ? "not-allowed" : "pointer" }}
        >
          <span>
            {submitting
              ? <><i className="fa fa-spinner fa-spin" style={{ marginRight: "8px" }}></i>Saving...</>
              : "Submit Quote Request"
            }
          </span>
        </button>
      </div>
    </form>
  );
}
